import { nip19 } from '@nostr/tools'
import * as kinds from '@nostr/tools/kinds'
import { normalizeUrl } from './url'

/**
 * NIP-XX (Commands) kind 31992 command definition.
 * See https://github.com/coracle-social/nips/blob/9726fe5a8a750ccfe03722e0a17c70a21068a1c7/CD.md
 */
export const SLASH_COMMAND_DEFINITION_KIND = 31992

export type TSlashCommandArg = {
  name: string
  type: string
  required: boolean
  label?: string
  choices: string[]
}

export type TSlashCommandDefinition = {
  d: string
  command?: string
  title?: string
  description?: string
  args: TSlashCommandArg[]
  scopes: string[]
  ignores: string[]
}

export function parseSlashCommandDefinition(tags: string[][]): TSlashCommandDefinition {
  const definition: TSlashCommandDefinition = { d: '', args: [], scopes: [], ignores: [] }

  for (const tag of tags) {
    switch (tag[0]) {
      case 'd':
        definition.d = tag[1] ?? ''
        break
      case 'command':
        definition.command = tag[1]
        break
      case 'title':
        definition.title = tag[1]
        break
      case 'description':
        definition.description = tag[1]
        break
      case 'arg':
        definition.args.push({
          name: tag[1] ?? '',
          type: tag[2] ?? 'text',
          required: tag[3] === 'required',
          label: tag[4],
          choices: tag.slice(5)
        })
        break
      case 's':
        if (tag[1]) definition.scopes.push(tag[1])
        break
      case 'ignore':
        if (tag[1]) definition.ignores.push(tag[1])
        break
    }
  }

  return definition
}

/**
 * Human-readable summary of scopes, e.g. "kind 1", "#h:abc", "npub1…".
 */
export function describeScopes(scopes: string[]): string[] {
  const descriptions: string[] = []

  for (const scope of scopes) {
    if (scope.startsWith('kind:')) {
      descriptions.push(scope.replace('kind:', 'kind '))
    } else if (scope.startsWith('relay:')) {
      descriptions.push(scope.replace('relay:', ''))
    } else if (scope.startsWith('author:')) {
      const value = scope.slice('author:'.length)
      try {
        descriptions.push(nip19.npubEncode(value))
      } catch {
        descriptions.push(scope)
      }
    } else if (scope.startsWith('tag:')) {
      descriptions.push(scope.replace('tag:', '#'))
    } else {
      descriptions.push(scope)
    }
  }

  return descriptions
}

/**
 * What we know about the event a slash command would be invoked in. Fields
 * left undefined are unknown: they don't restrict `s` scopes and don't
 * trigger `ignore` scopes.
 */
export type TSlashCommandTarget = {
  kind?: number
  tags?: string[][]
  /** the invoking author */
  pubkey?: string
  /** relays the event will be published to, when fixed */
  relays?: string[]
}

/** the group a scope belongs to: scopes in the same group are alternatives */
function scopeGroup(scope: string): string | null {
  const colon = scope.indexOf(':')
  if (colon === -1) return null
  const type = scope.slice(0, colon)
  switch (type) {
    case 'kind':
    case 'relay':
    case 'author':
      return type
    case 'tag': {
      const rest = scope.slice(colon + 1)
      const tagName = rest.split(':')[0]
      return tagName ? `tag:${tagName}` : null
    }
    default:
      return null
  }
}

/** true/false when the scope can be checked against the target, null when unknown */
function scopeMatches(scope: string, target: TSlashCommandTarget): boolean | null {
  const colon = scope.indexOf(':')
  const type = scope.slice(0, colon)
  const value = scope.slice(colon + 1)

  switch (type) {
    case 'kind':
      return target.kind === undefined ? null : target.kind === Number(value)
    case 'relay':
      return target.relays === undefined
        ? null
        : target.relays.some((url) => normalizeUrl(url) === normalizeUrl(value))
    case 'author':
      return target.pubkey === undefined ? null : target.pubkey === value
    case 'tag': {
      if (target.tags === undefined) return null
      const sep = value.indexOf(':')
      const tagName = sep === -1 ? value : value.slice(0, sep)
      const tagValue = sep === -1 ? undefined : value.slice(sep + 1)
      return target.tags.some(
        (tag) => tag[0] === tagName && (tagValue === undefined || tag[1] === tagValue)
      )
    }
    default:
      return null
  }
}

/**
 * Whether a command applies to the target event. Per the NIP, `s` scopes
 * combine like a filter (same type -- or same tag name -- are alternatives,
 * different types must all match) and any matching `ignore` scope excludes.
 */
export function slashCommandAppliesToEvent(
  definition: Pick<TSlashCommandDefinition, 'scopes' | 'ignores'>,
  target: TSlashCommandTarget
): boolean {
  const groups = new Map<string, string[]>()
  for (const scope of definition.scopes) {
    const group = scopeGroup(scope)
    if (!group) continue
    const list = groups.get(group)
    if (list) list.push(scope)
    else groups.set(group, [scope])
  }

  for (const scopes of groups.values()) {
    if (!scopes.some((scope) => scopeMatches(scope, target) !== false)) return false
  }

  return !definition.ignores.some((scope) => scopeMatches(scope, target) === true)
}

/**
 * A command is installable if the user could invoke it from a note or a
 * comment (the only kinds our composers produce).
 */
export function isSlashCommandInstallableForUser(
  definition: TSlashCommandDefinition,
  pubkey: string
) {
  return [kinds.ShortTextNote, kinds.Comment].some((kind) =>
    slashCommandAppliesToEvent(definition, { kind, pubkey })
  )
}

/** Relay URLs required by the command's `relay:` scopes. */
export function getSlashCommandRelayUrls(definition: Pick<TSlashCommandDefinition, 'scopes'>) {
  return definition.scopes
    .filter((scope) => scope.startsWith('relay:'))
    .map((scope) => scope.slice('relay:'.length))
    .filter(Boolean)
}

export type TSlashCommandInvocation = {
  /** the command trigger, without the leading slash */
  name: string
  /** hex pubkey of the targeted executor, if qualified with `@npub1…` */
  pubkey?: string
  /** length of the `/name[@qualifier]` part at the start of the content */
  length: number
}

const INVOCATION_REGEX = /^\/([^\s@/]+)(?:@(?:nostr:)?(npub1[02-9ac-hj-np-z]+))?(?=\s|$)/

/**
 * Parse an invocation at index 0 of `content`: `/<command>[@<npub>]`, with the
 * qualifier optionally `nostr:`-prefixed. An unparsable qualifier makes the
 * whole thing ordinary text.
 */
export function parseSlashCommandInvocation(content: string): TSlashCommandInvocation | null {
  const match = content.match(INVOCATION_REGEX)
  if (!match) return null

  if (!match[2]) return { name: match[1], length: match[0].length }

  try {
    const decoded = nip19.decode(match[2])
    if (decoded.type !== 'npub') return null
    return { name: match[1], pubkey: decoded.data, length: match[0].length }
  } catch {
    return null
  }
}

/**
 * Split the content after an invocation into positional args. A `text` arg
 * consumes the remainder verbatim.
 */
export function splitSlashCommandArgs(rest: string, args: TSlashCommandArg[]): string[] {
  const values: string[] = []
  let remaining = rest.replace(/^\s+/, '')

  for (const arg of args) {
    if (!remaining) break
    if (arg.type === 'text') {
      values.push(remaining.trimEnd())
      remaining = ''
      break
    }
    const word = remaining.match(/^\S+/)![0]
    values.push(word)
    remaining = remaining.slice(word.length).replace(/^\s+/, '')
  }

  return values
}

const BOOL_VALUES = ['true', 'false', 'yes', 'no', '1', '0', 't', 'f', 'y', 'n']
export const SLASH_COMMAND_BOOL_CHOICES = ['true', 'false']

function stripNostrPrefix(value: string) {
  return value.startsWith('nostr:') ? value.slice('nostr:'.length) : value
}

function decodeType(value: string): string | null {
  try {
    return nip19.decode(stripNostrPrefix(value)).type
  } catch {
    return null
  }
}

/** Whether `value` is acceptable for `arg`, per the NIP's argument types. */
export function isValidSlashCommandArgValue(arg: TSlashCommandArg, value: string): boolean {
  switch (arg.type) {
    case 'pubkey':
      return /^[0-9a-f]{64}$/.test(value) || ['npub', 'nprofile'].includes(decodeType(value) ?? '')
    case 'event':
      return /^[0-9a-f]{64}$/.test(value) || ['note', 'nevent'].includes(decodeType(value) ?? '')
    case 'address':
      return decodeType(value) === 'naddr'
    case 'relay':
      return /^wss?:\/\/\S+$/.test(value)
    case 'number':
      return /^\d+$/.test(value)
    case 'bool':
      return BOOL_VALUES.includes(value.toLowerCase())
    case 'enum':
      return arg.choices.length === 0 || arg.choices.includes(value)
    default:
      return true
  }
}

export type TSlashCommandArgProblem =
  | { type: 'missing'; arg: TSlashCommandArg }
  | { type: 'invalid'; arg: TSlashCommandArg; value: string }
  | { type: 'extra'; value: string }

/**
 * Problems with the args given to an invocation (the content after the
 * `/name[@npub]` part). Purely advisory: the NIP requires sending the content
 * anyway, as ordinary text if it doesn't parse.
 */
export function getSlashCommandArgProblems(
  definition: Pick<TSlashCommandDefinition, 'args'>,
  rest: string
): TSlashCommandArgProblem[] {
  const problems: TSlashCommandArgProblem[] = []
  const values = splitSlashCommandArgs(rest, definition.args)

  definition.args.forEach((arg, i) => {
    const value = values[i]
    if (value === undefined) {
      if (arg.required) problems.push({ type: 'missing', arg })
    } else if (!isValidSlashCommandArgValue(arg, value)) {
      problems.push({ type: 'invalid', arg, value })
    }
  })

  if (definition.args.at(-1)?.type !== 'text') {
    const extra = rest.trim().split(/\s+/).slice(definition.args.length).join(' ')
    if (extra) problems.push({ type: 'extra', value: extra })
  }

  return problems
}
